import { ukDate } from "../lib/dates";
import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../context/ConfirmContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  Phone, MessageSquare, MapPin, Search, PhoneCall, UserCheck, ArrowRightCircle,
  ClipboardList, CheckCircle2, Clock, Voicemail, PhoneMissed, Ban, Send, RotateCcw, AlertCircle, History, HeartHandshake, StickyNote, Download, CalendarDays, Users, Sparkles, UserPlus, Activity, ChevronDown, ArrowUp, ArrowDown, Trophy, Medal, Plus, XCircle, Star,
} from "lucide-react";
import { StatStrip } from "../components/StatStrip";
import PhoneActions from "../components/PhoneActions";
import { ContactActions } from "../components/ContactActions";
import { FirstTimersReport } from "../components/FirstTimersReport";
import { FirstTimerMarkDialog } from "../components/FirstTimerMarkDialog";
import { HandoverNoteDialog, HandoverNote } from "../components/HandoverNote";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";

const OUTCOMES = {
  call: [["reached", "Spoke / reached"], ["no_answer", "No answer"], ["voicemail", "Voicemail"], ["left_message", "Left message"], ["declined", "Declined / not interested"], ["other", "Other"]],
  text: [["sent", "Text sent"], ["replied", "Replied"], ["no_reply", "No reply"], ["other", "Other"]],
  visit: [["reached", "Met in person"], ["not_home", "Not home"], ["other", "Other"]],
};
const OUTCOME_LABEL = Object.values(OUTCOMES).flat().reduce((a, [k, v]) => ({ ...a, [k]: v }), {});
const KIND_ICON = { call: PhoneCall, text: MessageSquare, visit: MapPin };
const OUTCOME_ICON = { reached: CheckCircle2, replied: CheckCircle2, sent: Send, no_answer: PhoneMissed, no_reply: PhoneMissed, voicemail: Voicemail, left_message: Voicemail, declined: Ban, not_home: Ban, other: Clock };

const TABS = [
  { key: "pastoral", label: "Pastoral (New Members)" },
  { key: "follow_up", label: "Follow-Up" },
  { key: "all", label: "All Members" },
  { key: "activity", label: "Activity Log" },
  { key: "first_timers", label: "First-timers" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso), s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const OVERDUE_DAYS = 7;
function daysSince(iso) {
  if (!iso) return null;
  const t = iso.length === 10 ? `${iso}T00:00:00` : iso;
  const d = new Date(t);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}
function overdueDays(m) {
  if (m.care_stage === "integrated") return 0;
  const d = daysSince(m.last_contact_at || m.join_date);
  return d !== null && d >= OVERDUE_DAYS ? d : 0;
}

const SECTIONS = {
  pastoral: { title: "Pastoral Care", tabs: ["pastoral", "first_timers", "all", "activity"] },
  follow_up: { title: "Follow-up", tabs: ["follow_up", "activity"] },
};

export default function FollowUp({ section = "pastoral" }) {
  const location = useLocation();
  const SEC = SECTIONS[section];
  const [tab, setTab] = useState(SEC.tabs[0]);
  const [rows, setRows] = useState([]);
  const [activities, setActivities] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [logFor, setLogFor] = useState(null);
  const [form, setForm] = useState({ kind: "call", outcome: "reached", note: "" });
  const [saving, setSaving] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(!!location.state?.overdueOnly);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkChannel, setBulkChannel] = useState("sms");
  const [bulkMsg, setBulkMsg] = useState("Hi {first}, just checking in from Streams of Joy Erith London — we've been thinking of you and would love to see you soon. God bless!");
  const [bulkSending, setBulkSending] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [workload, setWorkload] = useState(null);
  const [workloadOpen, setWorkloadOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkActing, setBulkActing] = useState(false);
  const [bulkMode, setBulkMode] = useState("overdue"); // "overdue" | "selected"
  const [bulkCaller, setBulkCaller] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [hideIntegrated, setHideIntegrated] = useState(false);
  const [callerFilter, setCallerFilter] = useState("");
  const [bulkLogOpen, setBulkLogOpen] = useState(false);
  const [scope, setScope] = useState("month");
  const [carePage, setCarePage] = useState(1);
  const [carePageSize, setCarePageSize] = useState(() => Number(localStorage.getItem("sj_care_page_size")) || 25);
  const [careLetter, setCareLetter] = useState(() => localStorage.getItem("sj_care_letter") || "");
  useEffect(() => { localStorage.setItem("sj_care_page_size", String(carePageSize)); }, [carePageSize]);
  useEffect(() => { localStorage.setItem("sj_care_letter", careLetter); }, [careLetter]);
  const [contactedFor, setContactedFor] = useState(null);
  const [contactedList, setContactedList] = useState([]);
  const [contactedLoading, setContactedLoading] = useState(false);
  const confirm = useConfirm();
  const [ftMembers, setFtMembers] = useState(null);
  const [firstTimersOnly, setFirstTimersOnly] = useState(false);
  const [ftBulk, setFtBulk] = useState(false);

  const openContacted = async (caller) => {
    setContactedFor(caller);
    setContactedList([]);
    setContactedLoading(true);
    try {
      const params = new URLSearchParams();
      if (caller.id) params.set("caller_id", caller.id); else params.set("name", caller.name);
      params.set("scope", scope);
      const { data } = await api.get(`/care/caller-members?${params.toString()}`);
      setContactedList(data.members || []);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setContactedLoading(false); }
  };

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/care/summary"); setStats(data); } catch {}
    try { const { data } = await api.get("/care/workload"); setWorkload(data); } catch {}
  }, []);

  const openHistory = async (m) => {
    setHistoryFor(m);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/members/${m.id}/care-history`);
      setHistory(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setHistoryLoading(false); }
  };

  useEffect(() => {
    api.get("/care/team-members").then(({ data }) => setTeamMembers(data)).catch(() => {});
    loadStats();
  }, [loadStats]);

  const load = useCallback(async () => {
    if (tab === "first_timers") { setLoading(false); return; }
    setLoading(true);
    try {
      if (tab === "activity") {
        const { data } = await api.get("/care/activities", { params: { limit: 200 } });
        setActivities(data);
      } else {
        const { data } = await api.get("/care/board", { params: { scope: tab, search: search || undefined } });
        setRows(data);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [tab, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected([]); }, [tab]);

  const { user } = useAuth();
  const openLog = (m, kind = "call") => {
    setForm({ kind, outcome: OUTCOMES[kind][0][0], note: "", made_by: user?.name || "" });
    setLogFor(m);
  };
  const changeKind = (kind) => setForm((f) => ({ ...f, kind, outcome: OUTCOMES[kind][0][0] }));

  const overdueCount = rows.filter((m) => overdueDays(m) > 0).length;
  const showOverdueFilter = tab === "pastoral";
  const filteredRows = showOverdueFilter && overdueOnly ? rows.filter((m) => overdueDays(m) > 0) : rows;
  const careInitial = (m) => (m.last_name || m.first_name || m.name || "").trim().charAt(0).toUpperCase();
  const availableCareLetters = new Set(filteredRows.map(careInitial).filter(Boolean));
  const [sortBy, setSortBy] = useState("default");
  const displayedRows = React.useMemo(() => {
    let arr = [...filteredRows];
    if (firstTimersOnly) arr = arr.filter((m) => m.is_first_timer);
    if (hideIntegrated && tab === "all") arr = arr.filter((m) => m.care_stage !== "integrated");
    if (callerFilter === "__unassigned__") arr = arr.filter((m) => !m.assigned_caller);
    else if (callerFilter) arr = arr.filter((m) => m.assigned_caller === callerFilter);
    if (careLetter) arr = arr.filter((m) => (m.last_name || m.first_name || m.name || "").trim().charAt(0).toUpperCase() === careLetter);
    if (sortBy === "name") arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
    else if (sortBy === "joined_new") arr.sort((a, b) => (b.join_date || "").localeCompare(a.join_date || ""));
    else if (sortBy === "joined_old") arr.sort((a, b) => (a.join_date || "").localeCompare(b.join_date || ""));
    else if (sortBy === "overdue") arr.sort((a, b) => overdueDays(b) - overdueDays(a));
    else if (sortBy === "caller") arr.sort((a, b) => (a.assigned_caller || "~").localeCompare(b.assigned_caller || "~", undefined, { sensitivity: "base" }));
    return arr;
  }, [filteredRows, sortBy, hideIntegrated, tab, callerFilter, careLetter, firstTimersOnly]);
  const firstTimerCount = rows.filter((m) => m.is_first_timer).length;

  const careTotalPages = Math.max(1, Math.ceil(displayedRows.length / carePageSize));
  const careCurPage = Math.min(carePage, careTotalPages);
  const carePaged = displayedRows.slice((careCurPage - 1) * carePageSize, careCurPage * carePageSize);
  useEffect(() => { setCarePage(1); }, [tab, sortBy, callerFilter, overdueOnly, hideIntegrated, search, carePageSize, careLetter, firstTimersOnly]);
  const carePageNumbers = (() => {
    const out = [];
    for (let p = 1; p <= careTotalPages; p++) {
      if (p === 1 || p === careTotalPages || (p >= careCurPage - 1 && p <= careCurPage + 1)) out.push(p);
      else if (out[out.length - 1] !== "…") out.push("…");
    }
    return out;
  })();

  const [savedCareViews, setSavedCareViews] = useState(() => { try { return JSON.parse(localStorage.getItem("sj_care_views") || "[]"); } catch { return []; } });
  const [saveCareViewOpen, setSaveCareViewOpen] = useState(false);
  const [careViewName, setCareViewName] = useState("");
  const [defaultCareView, setDefaultCareView] = useState(() => localStorage.getItem("sj_care_default_view") || "");
  const appliedCareDefault = React.useRef(false);
  useEffect(() => { localStorage.setItem("sj_care_views", JSON.stringify(savedCareViews)); }, [savedCareViews]);
  useEffect(() => { localStorage.setItem("sj_care_default_view", defaultCareView); }, [defaultCareView]);
  const applyCareView = (v) => {
    setTab(SEC.tabs.includes(v.tab) ? v.tab : SEC.tabs[0]);
    setSortBy(v.sortBy ?? "default");
    setCallerFilter(v.callerFilter ?? "");
    setCareLetter(v.careLetter ?? "");
    setOverdueOnly(!!v.overdueOnly);
    setHideIntegrated(!!v.hideIntegrated);
    setSearch(v.search ?? "");
  };
  useEffect(() => {
    if (appliedCareDefault.current || !defaultCareView) return;
    const v = savedCareViews.find((x) => x.name === defaultCareView);
    if (v) { applyCareView(v); appliedCareDefault.current = true; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const saveCareView = () => {
    const name = careViewName.trim();
    if (!name) return;
    const v = { name, tab, sortBy, callerFilter, careLetter, overdueOnly, hideIntegrated, search };
    setSavedCareViews((prev) => [...prev.filter((x) => x.name !== name), v]);
    setCareViewName(""); setSaveCareViewOpen(false);
    toast.success(`Saved view "${name}"`);
  };
  const deleteCareView = (name) => { setSavedCareViews((prev) => prev.filter((x) => x.name !== name)); setDefaultCareView((d) => (d === name ? "" : d)); };
  const toggleDefaultCareView = (name) => setDefaultCareView((cur) => (cur === name ? "" : name));


  const sentText = async (message) => {
    try {
      const { data } = await api.post(`/members/${logFor.id}/contact`, { ...form, kind: "text", outcome: "sent", note: [form.note, `Text: ${message}`].filter(Boolean).join("\n") });
      if (data.handed_off) toast.info(`${logFor.name} auto handed off to the Follow-Up team.`);
      setLogFor(null); load(); loadStats();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const submitLog = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/members/${logFor.id}/contact`, form);
      if (data.handed_off) toast.info(`${logFor.name} auto handed off to the Follow-Up team after ${data.pastoral_call_attempts || 2} pastoral calls.`);
      else toast.success("Contact logged");
      setLogFor(null); load(); loadStats();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openBulkLog = (kind = "call") => {
    setForm({ kind, outcome: OUTCOMES[kind][0][0], note: "", made_by: user?.name || "" });
    setBulkLogOpen(true);
  };
  const submitBulkLog = async (e) => {
    e.preventDefault();
    if (selected.length === 0) return;
    setSaving(true);
    try {
      const results = await Promise.allSettled(selected.map((id) => api.post(`/members/${id}/contact`, form)));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok > 0) toast.success(`Logged a ${form.kind} for ${ok} member${ok > 1 ? "s" : ""}`);
      if (failed > 0) toast.error(`${failed} could not be logged`);
      setBulkLogOpen(false); setSelected([]); load(); loadStats();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const [handover, setHandover] = useState(null);
  const careAction = async (m, action, note) => {
    if (action === "handover" && note === undefined) { setHandover({ members: [m] }); return; }
    try {
      await api.post(`/members/${m.id}/care/${action}`, action === "handover" ? { note } : undefined);
      toast.success(action === "handover" ? `${m.name} handed to Follow-Up team` : action === "integrate" ? `${m.name} marked integrated 🎉` : "Moved back to pastoral care");
      load(); loadStats();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkAction = async (action, note) => {
    if (selected.length === 0) return;
    if (action === "handover" && note === undefined) { setHandover({ members: rows.filter((m) => selected.includes(m.id)), bulk: true }); return; }
    const names = rows.filter((m) => selected.includes(m.id)).map((m) => m.name);
    const verbTitle = action === "integrate" ? "Mark integrated?" : action === "handover" ? "Hand off to Follow-Up?" : "Move back to pastoral?";
    const preview = names.slice(0, 8).join(", ") + (names.length > 8 ? ` and ${names.length - 8} more` : "");
    const ok = action === "handover" ? true : await confirm({
      title: verbTitle,
      description: `This will ${action === "integrate" ? "mark as integrated" : action === "handover" ? "hand to the Follow-Up team" : "reopen pastoral care for"} ${selected.length} member${selected.length > 1 ? "s" : ""}: ${preview}.`,
      confirmLabel: action === "integrate" ? "Mark integrated" : action === "handover" ? "Hand off" : "Reopen",
      cancelLabel: "Cancel",
    });
    if (!ok) return;
    setBulkActing(true);
    try {
      const results = await Promise.allSettled(selected.map((id) => api.post(`/members/${id}/care/${action}`, action === "handover" ? { note } : undefined)));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      const verb = action === "integrate" ? "marked integrated 🎉" : action === "handover" ? "handed to Follow-Up" : "moved back to pastoral";
      if (ok > 0) toast.success(`${ok} member${ok > 1 ? "s" : ""} ${verb}`);
      if (failed > 0) toast.error(`${failed} could not be updated`);
      setSelected([]); load(); loadStats();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBulkActing(false); }
  };

  const assignCaller = async () => {
    if (selected.length === 0) return;
    setBulkAssigning(true);
    try {
      const { data } = await api.post("/care/assign", { member_ids: selected, caller: bulkCaller });
      toast.success(bulkCaller ? `${data.updated} member(s) assigned to ${bulkCaller}` : `Caller cleared for ${data.updated} member(s)`);
      setSelected([]); setBulkCaller(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBulkAssigning(false); }
  };

  const sendBulkCheckIn = async () => {
    setBulkSending(true);
    try {
      const { data } = bulkMode === "selected"
        ? await api.post("/care/message", { member_ids: selected, channel: bulkChannel, message: bulkMsg })
        : await api.post("/care/overdue/message", { channel: bulkChannel, message: bulkMsg });
      const real = data.sent, sim = data.simulated;
      if (sim > 0 && real === 0) {
        toast.warning(`Simulated ${sim} ${bulkChannel === "sms" ? "text" : "email"}(s) — add ${bulkChannel === "sms" ? "Twilio" : "Resend"} keys to send for real.`);
      } else {
        toast.success(`Check-in sent to ${real} member(s).`);
      }
      if (data.skipped_no_contact > 0) toast.info(`${data.skipped_no_contact} member(s) skipped (no ${bulkChannel === "sms" ? "phone" : "email"} on file).`);
      setBulkOpen(false); if (bulkMode === "selected") setSelected([]); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBulkSending(false); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="follow-up-page">
      <HandoverNoteDialog target={handover} onClose={() => setHandover(null)}
        onSubmit={(note) => { const t = handover; setHandover(null); if (t.bulk) bulkAction("handover", note); else careAction(t.members[0], "handover", note); }} />
      <FirstTimerMarkDialog members={ftMembers} bulkMode={ftBulk} onClose={() => setFtMembers(null)} onSaved={() => { if (ftBulk) setSelected([]); load(); loadStats(); }} />
      <div className="sj-page-hero">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em]" style={{ color: "var(--sj-text-3)" }}>
          <PhoneCall className="h-4 w-4" /> Pastoral care & follow-up
        </div>
        <h1 className="mt-2 font-head text-3xl font-extrabold tracking-tight" data-testid="care-page-title">{SEC.title}</h1>
        <p style={{ color: "var(--sj-text-2)" }}>Track new members, log calls &amp; texts, and hand them from pastoral care to the Follow-Up team.</p>
      </div>

      {stats && (
        <StatStrip testid="care-stats" cols={6} items={[
          { key: "in_pastoral", label: "In pastoral care", value: stats.in_pastoral, icon: HeartHandshake },
          { key: "in_follow_up", label: "In follow-up", value: stats.in_follow_up, icon: PhoneCall },
          { key: "overdue", label: "Overdue (7+ days)", value: stats.overdue, icon: AlertCircle },
          { key: "contacts_this_week", label: "Contacts this week", value: stats.contacts_this_week, icon: Activity },
          { key: "new_this_month", label: "New this month", value: stats.new_this_month, icon: UserPlus },
          { key: "converts", label: "New believers", value: stats.converts, icon: Sparkles },
        ]} />
      )}

      {workload?.me && (() => {
        const meMembers = scope === "month" ? (workload.me.month_members || 0) : (workload.me.members || 0);
        const meCount = scope === "month" ? (workload.me.count || 0) : (workload.me.all_count || 0);
        return (
        <div className="rounded-2xl px-5 py-4 flex items-center gap-4 flex-wrap" data-testid="my-contact-tally"
          style={{ background: "var(--sj-primary)", color: "#fff" }}>
          <UserCheck className="h-7 w-7 shrink-0" />
          <div>
            <div className="text-3xl font-extrabold leading-none" data-testid="my-contact-count">{meMembers}</div>
            <div className="text-sm opacity-90 mt-0.5">member{meMembers === 1 ? "" : "s"} you've contacted &amp; logged {scope === "month" ? `in ${workload.month}` : "all-time"}</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right text-xs opacity-90 leading-tight">
              <div className="font-semibold text-sm">{meCount} contact{meCount === 1 ? "" : "s"}</div>
              <div>{scope === "month" ? `logged in ${workload.month}` : "logged all-time"}</div>
            </div>
            <div className="inline-flex rounded-full p-0.5" style={{ background: "rgba(255,255,255,0.2)" }} data-testid="tally-scope-toggle">
              {[["month", "This month"], ["all", "All-time"]].map(([k, label]) => (
                <button key={k} type="button" data-testid={`tally-scope-${k}`} onClick={() => setScope(k)}
                  className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
                  style={{ background: scope === k ? "#fff" : "transparent", color: scope === k ? "var(--sj-primary)" : "#fff" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        );
      })()}

      {workload && workload.callers && workload.callers.length > 0 && (
        <div className={`${CARD} p-5`} data-testid="care-workload">
          <button type="button" data-testid="care-workload-toggle" onClick={() => setWorkloadOpen((o) => !o)}
            className="w-full flex items-center gap-2 text-left">
            <PhoneCall className="h-4 w-4" style={{ color: "var(--sj-primary)" }} />
            <h2 className="font-head text-lg font-bold" style={{ color: "var(--sj-text)" }}>Caller workload</h2>
            <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>· {scope === "month" ? workload.month : "all-time"}</span>
            {!workloadOpen && workload.least_busy && workload.callers.length > 1 && (
              <span className="hidden sm:inline text-xs font-medium truncate" style={{ color: "var(--sj-primary)" }}>· {workload.least_busy.name} is lightest</span>
            )}
            <ChevronDown className="h-4 w-4 ml-auto shrink-0 transition-transform" style={{ color: "var(--sj-text-3)", transform: workloadOpen ? "rotate(180deg)" : "none" }} />
          </button>
          {workloadOpen && (
            <div className="mt-3">
              {(() => {
                const top = workload.callers.filter((c) => (c.count || 0) > 0).slice(0, 3);
                if (top.length === 0) return null;
                const medals = ["#F5B301", "#B8B8C0", "#CD7F32"];
                return (
                  <div className="rounded-xl px-3.5 py-3 mb-3" data-testid="care-leaderboard" style={{ background: "#0B5E4E0A", border: "1px solid #0B5E4E26" }}>
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "var(--sj-primary)" }}>
                      <Trophy className="h-3.5 w-3.5" /> Top callers · {workload.month}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {top.map((c, i) => (
                        <div key={i} data-testid={`care-leaderboard-${i}`} className="inline-flex items-center gap-2 rounded-full bg-white pl-2 pr-3 py-1.5 shadow-sm">
                          <Medal className="h-4 w-4" style={{ color: medals[i] }} />
                          <span className="text-sm font-semibold" style={{ color: "var(--sj-text)" }}>{c.name}</span>
                          <span className="text-xs font-bold" style={{ color: "var(--sj-primary)" }}>{c.count} this month</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {workload.least_busy && workload.callers.length > 1 && (
                <div className="flex items-center gap-2 rounded-xl px-3.5 py-2.5 mb-3 text-sm" data-testid="care-workload-hint" style={{ background: "#0B5E4E0F", color: "var(--sj-primary)" }}>
                  <Sparkles className="h-4 w-4 shrink-0" />
                  <span><strong>{workload.least_busy.name}</strong> has the lightest load ({workload.least_busy.count} this month) — a good person to give the next call to.</span>
                </div>
              )}
              <div className="space-y-2.5">
                {(() => {
                  const val = (c) => scope === "month" ? (c.count || 0) : (c.all_count || 0);
                  const maxLoad = Math.max(1, ...workload.callers.map(val));
                  return workload.callers.map((c, i) => {
                    const isMe = user && c.id && String(c.id) === String(user.id);
                    const cMembers = scope === "month" ? (c.month_members || 0) : (c.members || 0);
                    const cCalls = scope === "month" ? (c.calls || 0) : (c.all_calls || 0);
                    const cTexts = scope === "month" ? (c.texts || 0) : (c.all_texts || 0);
                    const cCount = val(c);
                    return (
                    <div key={`${c.name}-${i}`} className="flex items-center gap-3 rounded-xl px-2 py-1 -mx-2" data-testid={`care-workload-row-${i}`}
                      style={isMe ? { background: "#0B5E4E14" } : undefined}>
                      <span className="w-44 shrink-0 truncate text-sm font-medium flex items-center gap-1.5" style={{ color: "var(--sj-text)" }}>
                        {c.name}
                        {isMe && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "var(--sj-primary)", color: "#fff" }}>You</span>}
                      </span>
                      <div className="flex-1 h-2.5 rounded-full overflow-hidden" style={{ background: "var(--sj-bg)" }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${(cCount / maxLoad) * 100}%`, background: "var(--sj-primary)" }} />
                      </div>
                      <button type="button" onClick={() => openContacted(c)} data-testid={`care-workload-contacted-${i}`}
                        title="Members this caller has contacted &amp; logged — click to view the list"
                        className="w-28 text-right text-xs font-medium hover:underline" style={{ color: cMembers ? "var(--sj-primary)" : "var(--sj-text-3)" }}>{cMembers} contacted</button>
                      <span className="hidden sm:inline w-24 text-right text-xs" style={{ color: "var(--sj-text-3)" }}>{cCalls} calls · {cTexts} texts</span>
                      <button type="button" data-testid={`care-workload-assigned-${i}`} onClick={() => { setTab("all"); setCallerFilter(c.name); }}
                        title={`Filter to ${c.name}'s assigned members`}
                        className="w-24 text-right text-xs font-medium hover:underline" style={{ color: c.assigned ? "var(--sj-primary)" : "var(--sj-text-3)" }}>
                        {c.assigned || 0} assigned
                      </button>
                      <span className="w-8 text-right text-sm font-bold" style={{ color: "var(--sj-text-2)" }}>{cCount}</span>
                    </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!contactedFor} onOpenChange={(o) => { if (!o) setContactedFor(null); }}>
        <DialogContent className="max-w-lg" data-testid="contacted-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
              {contactedFor?.name} · members contacted
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs -mt-2 mb-1" style={{ color: "var(--sj-text-3)" }}>
            {scope === "month" ? `Logged in ${workload?.month}` : "All-time"} · {contactedList.length} member{contactedList.length === 1 ? "" : "s"}
          </p>
          <div className="max-h-[420px] overflow-y-auto space-y-1.5 pr-1">
            {contactedLoading && <div className="py-8 text-center text-sm" style={{ color: "var(--sj-text-3)" }}>Loading…</div>}
            {!contactedLoading && contactedList.length === 0 && (
              <div className="py-8 text-center text-sm" data-testid="contacted-empty" style={{ color: "var(--sj-text-3)" }}>No members logged in this period yet.</div>
            )}
            {!contactedLoading && contactedList.map((m, i) => {
              const Icon = KIND_ICON[m.kind] || PhoneCall;
              return (
                <div key={m.member_id} data-testid={`contacted-row-${i}`} className="flex items-center gap-3 rounded-xl px-3 py-2" style={{ background: "var(--sj-bg)" }}>
                  <Icon className="h-4 w-4 shrink-0" style={{ color: "var(--sj-primary)" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate" style={{ color: "var(--sj-text)" }}>{m.name || "Member"}</div>
                    <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>
                      {OUTCOME_LABEL[m.outcome] || m.kind} · {timeAgo(m.at)}
                    </div>
                  </div>
                  <span className="text-xs font-medium shrink-0" style={{ color: "var(--sj-text-2)" }}>{m.contacts} log{m.contacts === 1 ? "" : "s"}</span>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <button type="button" data-testid="contacted-close" onClick={() => setContactedFor(null)}
              className="rounded-xl px-4 py-2 text-sm font-semibold" style={{ background: "var(--sj-primary)", color: "#fff" }}>Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <div className="flex flex-wrap gap-2">
        {TABS.filter((t) => SEC.tabs.includes(t.key)).map((t) => {
          const showOverdue = t.key === "pastoral" && tab === "pastoral" && overdueCount > 0;
          return (
            <button key={t.key} data-testid={`care-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="rounded-full px-5 py-2 text-sm font-semibold transition-all inline-flex items-center gap-2"
              style={{ background: tab === t.key ? "var(--sj-primary)" : "transparent", color: tab === t.key ? "#fff" : "var(--sj-text-2)", border: tab === t.key ? "none" : "1px solid var(--sj-border)" }}>
              {t.label}
              {showOverdue && (
                <span data-testid="overdue-count" className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold" style={{ background: "#fff", color: "#C0392B" }}>{overdueCount}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab !== "activity" && tab !== "first_timers" && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--sj-primary)" }} />
            <input data-testid="care-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or phone" className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm pl-9 pr-4 py-2.5 text-sm" />
          </div>
          <select data-testid="care-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={inputCls} style={{ maxWidth: 220 }}>
            <option value="default">Sort: Recently contacted</option>
            <option value="overdue">Sort: Most overdue first</option>
            <option value="name">Sort: Name (A–Z)</option>
            <option value="joined_new">Sort: Date joined (newest)</option>
            <option value="joined_old">Sort: Date joined (oldest)</option>
          </select>
          <select data-testid="caller-filter" value={callerFilter} onChange={(e) => setCallerFilter(e.target.value)} className={inputCls} style={{ maxWidth: 200 }}>
            <option value="">All callers</option>
            <option value="__unassigned__">Unassigned</option>
            {teamMembers.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
          {user?.name && (
            <button data-testid="just-mine-toggle" onClick={() => setCallerFilter((c) => c === user.name ? "" : user.name)}
              className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold border transition-all shrink-0"
              style={{ borderColor: callerFilter === user.name ? "var(--sj-primary)" : "var(--sj-border)", background: callerFilter === user.name ? "var(--sj-primary)" : "#fff", color: callerFilter === user.name ? "#fff" : "var(--sj-text-2)" }}>
              <UserCheck className="h-4 w-4" /> Just mine
            </button>
          )}
          <button data-testid="first-timers-only-toggle" onClick={() => setFirstTimersOnly((v) => !v)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold border transition-all shrink-0"
            style={{ borderColor: firstTimersOnly ? "#8a6a00" : "var(--sj-border)", background: firstTimersOnly ? "#FEF6D8" : "#fff", color: firstTimersOnly ? "#8a6a00" : "var(--sj-text-2)" }}>
            <Star className="h-4 w-4" fill={firstTimersOnly ? "currentColor" : "none"} /> First-timers only
            <span data-testid="first-timers-only-count" className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold" style={{ background: "#8a6a001A", color: "#8a6a00" }}>{firstTimerCount}</span>
          </button>
          {tab === "all" && (
            <button
              data-testid="hide-integrated-toggle"
              onClick={() => setHideIntegrated((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold border transition-all"
              style={{ borderColor: hideIntegrated ? "var(--sj-primary)" : "var(--sj-border)", background: hideIntegrated ? "var(--sj-primary)" : "#fff", color: hideIntegrated ? "#fff" : "var(--sj-text-2)" }}
            >
              <UserCheck className="h-4 w-4" /> {hideIntegrated ? "Integrated hidden" : "Hide integrated"}
            </button>
          )}
          {showOverdueFilter && (
            <button
              data-testid="overdue-filter-toggle"
              onClick={() => setOverdueOnly((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold border transition-all"
              style={{
                borderColor: overdueOnly ? "#C0392B" : "var(--sj-border)",
                background: overdueOnly ? "#C0392B" : "#fff",
                color: overdueOnly ? "#fff" : "var(--sj-text-2)",
              }}
            >
              <AlertCircle className="h-4 w-4" /> Overdue only
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold" style={{ background: overdueOnly ? "#fff" : "#C0392B1A", color: "#C0392B" }}>{overdueCount}</span>
            </button>
          )}
          {showOverdueFilter && overdueCount > 0 && (
            <button
              data-testid="bulk-checkin-button"
              onClick={() => { setBulkMode("overdue"); setBulkOpen(true); }}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white transition-all hover:brightness-105"
              style={{ background: "var(--sj-secondary)" }}
            >
              <Send className="h-4 w-4" /> Check in with all overdue
            </button>
          )}
        </div>
      )}

      {tab !== "activity" && tab !== "first_timers" && selected.length > 0 && (
        <div data-testid="care-bulk-bar" className="sticky top-2 z-10 flex flex-wrap items-center gap-2 rounded-2xl px-4 py-3 shadow-[0_8px_30px_rgba(45,45,45,0.10)]" style={{ background: "var(--sj-primary)" }}>
          <span className="text-sm font-bold text-white" data-testid="care-bulk-count">{selected.length} selected</span>
          <div className="flex items-center gap-1.5 ml-1">
            <select data-testid="bulk-caller-select" value={bulkCaller} onChange={(e) => setBulkCaller(e.target.value)} className="rounded-full px-3 py-1.5 text-sm bg-white/95 outline-none" style={{ color: "var(--sj-text-2)" }}>
              <option value="">Assign caller…</option>
              {teamMembers.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
            <button data-testid="bulk-assign-btn" disabled={bulkAssigning || !bulkCaller} onClick={assignCaller} className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold border border-white/60 text-white hover:bg-white/10 disabled:opacity-50">
              <UserPlus className="h-4 w-4" /> {bulkAssigning ? "…" : "Assign"}
            </button>
          </div>
          <div className="flex-1" />
          <button data-testid="bulk-log-btn" onClick={() => openBulkLog("call")} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border border-white/60 text-white hover:bg-white/10">
            <Phone className="h-4 w-4" /> Log contact
          </button>
          <button data-testid="bulk-message-btn" onClick={() => { setBulkMode("selected"); setBulkOpen(true); }} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border border-white/60 text-white hover:bg-white/10">
            <Send className="h-4 w-4" /> Send check-in
          </button>
          <button data-testid="bulk-first-timer-btn" onClick={() => { setFtBulk(true); setFtMembers(rows.filter((r) => selected.includes(r.id))); }} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border border-white/60 text-white hover:bg-white/10">
            <Star className="h-4 w-4" /> Mark first-timers
          </button>
          <button data-testid="bulk-integrate-btn" disabled={bulkActing} onClick={() => bulkAction("integrate")} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold bg-white hover:brightness-95 disabled:opacity-60" style={{ color: "#17A88A" }}>
            <UserCheck className="h-4 w-4" /> {bulkActing ? "Working…" : "Mark integrated"}
          </button>
          {tab === "pastoral" && (
            <button data-testid="bulk-handoff-btn" disabled={bulkActing} onClick={() => bulkAction("handover")} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold border border-white/60 text-white hover:bg-white/10 disabled:opacity-60">
              <ArrowRightCircle className="h-4 w-4" /> Hand to Follow-Up
            </button>
          )}
          <button data-testid="bulk-clear-btn" onClick={() => setSelected([])} className="text-sm font-medium text-white/90 underline underline-offset-2 px-2">Clear</button>
        </div>
      )}

      {tab !== "activity" && tab !== "first_timers" && !loading && (
        <div className="flex flex-wrap items-center gap-2" data-testid="care-saved-views">
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Saved views</span>
          {savedCareViews.length === 0 && <span className="text-xs" style={{ color: "var(--sj-text-3)" }}>None yet — set a tab/filter/sort, then save it.</span>}
          {savedCareViews.map((v) => (
            <span key={v.name} className="inline-flex items-center gap-1 rounded-full pl-1 pr-1 py-1 text-xs font-medium" style={{ background: "#0B5E4E0F", color: "var(--sj-primary)", border: "1px solid #0B5E4E33" }}>
              <button type="button" data-testid={`care-pin-view-${v.name}`} onClick={() => toggleDefaultCareView(v.name)}
                title={defaultCareView === v.name ? "Default view — opens automatically (tap to unpin)" : "Set as default (opens automatically)"}
                className="h-4 w-4 inline-flex items-center justify-center">
                <Star className="h-3.5 w-3.5" style={{ fill: defaultCareView === v.name ? "currentColor" : "none" }} />
              </button>
              <button type="button" data-testid={`care-apply-view-${v.name}`} onClick={() => applyCareView(v)} className="hover:underline px-0.5">{v.name}</button>
              <button type="button" data-testid={`care-delete-view-${v.name}`} onClick={() => deleteCareView(v.name)} title="Delete view" className="h-4 w-4 inline-flex items-center justify-center rounded-full hover:bg-[#0B5E4E1A]"><XCircle className="h-3.5 w-3.5" /></button>
            </span>
          ))}
          <button type="button" data-testid="care-save-view-btn" onClick={() => setSaveCareViewOpen(true)} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold border transition-colors hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
            <Plus className="h-3.5 w-3.5" /> Save this view
          </button>
        </div>
      )}

      <Dialog open={saveCareViewOpen} onOpenChange={setSaveCareViewOpen}>
        <DialogContent data-testid="care-save-view-dialog" className="max-w-sm">
          <DialogHeader><DialogTitle className="font-head text-xl">Save this view</DialogTitle></DialogHeader>
          <p className="text-xs -mt-1" style={{ color: "var(--sj-text-3)" }}>
            Captures the current tab, caller filter, A–Z letter, sort{overdueOnly ? ", overdue-only" : ""}{hideIntegrated ? ", hide-integrated" : ""} and search.
          </p>
          <input data-testid="care-view-name-input" autoFocus value={careViewName} onChange={(e) => setCareViewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") saveCareView(); }}
            placeholder="e.g. My overdue callbacks" className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 px-4 py-2.5 text-sm mt-1" />
          <DialogFooter>
            <button type="button" onClick={() => setSaveCareViewOpen(false)} className="rounded-xl px-4 py-2 text-sm font-medium" style={{ border: "1px solid var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
            <button type="button" data-testid="care-save-view-confirm" onClick={saveCareView} disabled={!careViewName.trim()} className="rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--sj-primary)" }}>Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {tab !== "activity" && tab !== "first_timers" && !loading && filteredRows.length > 0 && (
        <div className="flex flex-wrap items-center gap-1" data-testid="care-az-jump">
          <button type="button" data-testid="care-az-all" onClick={() => setCareLetter("")}
            className="min-w-[30px] px-2 py-1 rounded-md text-xs font-semibold transition-colors"
            style={careLetter === "" ? { background: "var(--sj-primary)", color: "#fff" } : { color: "var(--sj-text-2)", border: "1px solid var(--sj-border)" }}>All</button>
          {"ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((ch) => {
            const has = availableCareLetters.has(ch);
            const active = careLetter === ch;
            return (
              <button key={ch} type="button" data-testid={`care-az-${ch}`} disabled={!has}
                onClick={() => setCareLetter(active ? "" : ch)}
                className="min-w-[30px] px-2 py-1 rounded-md text-xs font-semibold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                style={active ? { background: "var(--sj-primary)", color: "#fff" } : { color: has ? "var(--sj-text-2)" : "var(--sj-text-3)", border: "1px solid var(--sj-border)" }}>
                {ch}
              </button>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className={`${CARD} p-12 text-center`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>
      ) : tab === "first_timers" ? (
        <FirstTimersReport />
      ) : tab === "activity" ? (
        <ActivityFeed activities={activities} />
      ) : displayedRows.length === 0 ? (
        <div className={`${CARD} p-16 text-center flex flex-col items-center gap-3`} style={{ color: "var(--sj-text-3)" }}>
          <ClipboardList className="h-12 w-12" />
          {overdueOnly && tab === "pastoral" ? "No overdue members — everyone's been contacted in the last 7 days. 🎉" : tab === "pastoral" ? "No new members in pastoral care right now." : tab === "follow_up" ? "No members in the Follow-Up queue." : "No members found."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="care-list">
          <div className="hidden md:flex items-center gap-4 px-4 pb-1" data-testid="care-list-headings">
            <input type="checkbox" data-testid="care-select-all"
              checked={displayedRows.length > 0 && displayedRows.every((m) => selected.includes(m.id))}
              onChange={(e) => setSelected(e.target.checked ? displayedRows.map((m) => m.id) : [])}
              className="h-4 w-4 accent-[#0B5E4E] shrink-0" title="Select all" />
            <div className="min-w-0 flex-1 flex items-center gap-6">
              <button data-testid="care-head-name" onClick={() => setSortBy("name")}
                className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-opacity hover:opacity-70"
                style={{ color: sortBy === "name" ? "var(--sj-primary)" : "var(--sj-text-3)" }}>
                Name {sortBy === "name" && <ArrowDown className="h-3 w-3" />}
              </button>
              <button data-testid="care-head-default" onClick={() => setSortBy("default")}
                className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-opacity hover:opacity-70"
                style={{ color: sortBy === "default" ? "var(--sj-primary)" : "var(--sj-text-3)" }}>
                Last contacted {sortBy === "default" && <ArrowDown className="h-3 w-3" />}
              </button>
              <button data-testid="care-head-joined" onClick={() => setSortBy((s) => (s === "joined_new" ? "joined_old" : "joined_new"))}
                className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-opacity hover:opacity-70"
                style={{ color: sortBy.startsWith("joined") ? "var(--sj-primary)" : "var(--sj-text-3)" }}>
                Joined {sortBy === "joined_new" ? <ArrowDown className="h-3 w-3" /> : sortBy === "joined_old" ? <ArrowUp className="h-3 w-3" /> : null}
              </button>
            </div>
            {tab === "pastoral" && (
              <button data-testid="care-head-overdue" onClick={() => setSortBy("overdue")}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-opacity hover:opacity-70"
                style={{ color: sortBy === "overdue" ? "var(--sj-primary)" : "var(--sj-text-3)" }}>
                Calls {sortBy === "overdue" && <ArrowDown className="h-3 w-3" />}
              </button>
            )}
            <button data-testid="care-head-caller" onClick={() => setSortBy("caller")}
              className="shrink-0 w-32 text-left inline-flex items-center justify-start gap-1 text-xs font-semibold uppercase tracking-wider transition-opacity hover:opacity-70"
              style={{ color: sortBy === "caller" ? "var(--sj-primary)" : "var(--sj-text-3)" }}>
              Caller {sortBy === "caller" && <ArrowDown className="h-3 w-3" />}
            </button>
            <span className="shrink-0 w-[280px] text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Actions</span>
          </div>
          {carePaged.map((m) => <MemberRow key={m.id} m={m} tab={tab} onLog={openLog} onAction={careAction} onHistory={openHistory} onFirstTimer={(x) => { setFtBulk(false); setFtMembers([x]); }} selected={selected.includes(m.id)} onToggleSelect={toggleSelect} />)}
          {displayedRows.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-2 pt-2" data-testid="care-pagination">
              <div className="flex items-center gap-3 text-xs" style={{ color: "var(--sj-text-3)" }}>
                <span>Showing <strong style={{ color: "var(--sj-text-2)" }}>{(careCurPage - 1) * carePageSize + 1}–{Math.min(careCurPage * carePageSize, displayedRows.length)}</strong> of {displayedRows.length}</span>
                <label className="flex items-center gap-1.5">
                  <span>Per page</span>
                  <select data-testid="care-page-size" value={carePageSize} onChange={(e) => setCarePageSize(Number(e.target.value))}
                    className="bg-white border rounded-lg px-2 py-1 text-xs outline-none focus:border-[#0B5E4E]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                    {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
              </div>
              {careTotalPages > 1 && (
                <div className="flex items-center gap-1.5">
                  <button type="button" data-testid="care-prev-page" disabled={careCurPage <= 1} onClick={() => setCarePage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#EDF2F4]" style={{ border: "1px solid var(--sj-border)", color: "var(--sj-text-2)" }}>Prev</button>
                  {carePageNumbers.map((p, idx) => p === "…" ? (
                    <span key={`gap-${idx}`} className="px-1.5 text-sm" style={{ color: "var(--sj-text-3)" }}>…</span>
                  ) : (
                    <button key={p} type="button" data-testid={`care-page-${p}`} onClick={() => setCarePage(p)}
                      className="min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold transition-colors"
                      style={p === careCurPage ? { background: "var(--sj-primary)", color: "#fff" } : { border: "1px solid var(--sj-border)", color: "var(--sj-text-2)" }}>{p}</button>
                  ))}
                  <button type="button" data-testid="care-next-page" disabled={careCurPage >= careTotalPages} onClick={() => setCarePage((p) => Math.min(careTotalPages, p + 1))}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#EDF2F4]" style={{ border: "1px solid var(--sj-border)", color: "var(--sj-text-2)" }}>Next</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent data-testid="bulk-checkin-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">{bulkMode === "selected" ? "Send check-in to selected" : "Check in with all overdue"}</DialogTitle></DialogHeader>
          <p className="text-sm mt-1" style={{ color: "var(--sj-text-2)" }}>
            {bulkMode === "selected"
              ? <>Send a warm check-in to the <strong>{selected.length}</strong> selected member(s).</>
              : <>Send a warm check-in to the <strong>{overdueCount}</strong> member(s) not contacted in 7+ days.</>} Use <code>{"{first}"}</code> to insert their first name.
          </p>
          {bulkMode === "selected" && selected.length > 0 && (
            <div data-testid="bulk-checkin-names" className="mt-2 flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
              {rows.filter((m) => selected.includes(m.id)).map((m) => (
                <span key={m.id} className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ background: "#EDF2F4", color: "var(--sj-text-2)" }}>{m.name}</span>
              ))}
            </div>
          )}
          <div className="mt-3">
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Channel</label>
            <div className="flex gap-2">
              {[["sms", "Text (SMS)"], ["email", "Email"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`bulk-channel-${v}`} onClick={() => setBulkChannel(v)}
                  className="flex-1 rounded-xl py-2.5 text-sm font-medium border transition-all"
                  style={bulkChannel === v ? { background: "var(--sj-secondary)", color: "#fff", borderColor: "var(--sj-secondary)" } : { borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-3">
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Message</label>
            <textarea data-testid="bulk-message" rows={4} value={bulkMsg} onChange={(e) => setBulkMsg(e.target.value)} className={inputCls} />
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setBulkOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
            <button type="button" data-testid="bulk-send-button" disabled={bulkSending || !bulkMsg.trim()} onClick={sendBulkCheckIn}
              className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all disabled:opacity-60 inline-flex items-center gap-2" style={{ background: "var(--sj-primary)" }}>
              <Send className="h-4 w-4" /> {bulkSending ? "Sending…" : `Send to ${bulkMode === "selected" ? selected.length : overdueCount}`}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!logFor} onOpenChange={(o) => !o && setLogFor(null)}>
        <DialogContent data-testid="contact-dialog" className="max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head text-2xl">{form.kind === "call" ? "Call" : form.kind === "text" ? "Text" : "Log visit"} · {logFor?.name}</DialogTitle><DialogDescription className="sr-only">Contact the member and record the outcome</DialogDescription></DialogHeader>
          <form onSubmit={submitLog} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Type</label>
              <div className="flex gap-2">
                {["call", "text", "visit"].map((k) => {
                  const Icon = KIND_ICON[k];
                  return (
                    <button key={k} type="button" data-testid={`kind-${k}`} onClick={() => changeKind(k)}
                      className="flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-medium capitalize border transition-all"
                      style={form.kind === k ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                      <Icon className="h-4 w-4" /> {k}
                    </button>
                  );
                })}
              </div>
            </div>
            {logFor && <ContactActions key={form.kind} member={logFor} kind={form.kind} onCalled={() => {}} onSent={sentText} />}
            <div className="text-xs font-semibold uppercase tracking-wider pt-1" style={{ color: "var(--sj-text-3)" }}>Record the outcome</div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Made by</label>
              <select data-testid="contact-made-by" value={form.made_by} onChange={(e) => setForm({ ...form, made_by: e.target.value })} className={inputCls}>
                {teamMembers.length === 0 && <option value={form.made_by}>{form.made_by || "Me"}</option>}
                {teamMembers.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Outcome</label>
              <select data-testid="contact-outcome" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} className={inputCls}>
                {OUTCOMES[form.kind].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <textarea data-testid="contact-note" placeholder="Notes (prayer points, next steps…)" rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} />
            <DialogFooter>
              <button type="button" onClick={() => setLogFor(null)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" disabled={saving} data-testid="contact-submit" className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{saving ? "Saving…" : "Save log"}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkLogOpen} onOpenChange={setBulkLogOpen}>
        <DialogContent data-testid="bulk-contact-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Log contact · {selected.length} member{selected.length > 1 ? "s" : ""}</DialogTitle></DialogHeader>
          <p className="text-sm mt-1" style={{ color: "var(--sj-text-3)" }}>Records the same outcome for everyone selected. Pastoral members who reach their 2nd call may auto-hand off to Follow-Up.</p>
          <form onSubmit={submitBulkLog} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Type</label>
              <div className="flex gap-2">
                {["call", "text", "visit"].map((k) => {
                  const Icon = KIND_ICON[k];
                  return (
                    <button key={k} type="button" data-testid={`bulk-kind-${k}`} onClick={() => changeKind(k)}
                      className="flex-1 flex items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-medium capitalize border transition-all"
                      style={form.kind === k ? { background: "var(--sj-primary)", color: "#fff", borderColor: "var(--sj-primary)" } : { borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
                      <Icon className="h-4 w-4" /> {k}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Made by</label>
              <select data-testid="bulk-contact-made-by" value={form.made_by} onChange={(e) => setForm({ ...form, made_by: e.target.value })} className={inputCls}>
                {teamMembers.length === 0 && <option value={form.made_by}>{form.made_by || "Me"}</option>}
                {teamMembers.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Outcome</label>
              <select data-testid="bulk-contact-outcome" value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} className={inputCls}>
                {OUTCOMES[form.kind].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <textarea data-testid="bulk-contact-note" placeholder="Notes applied to each member (optional)" rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputCls} />
            <DialogFooter>
              <button type="button" onClick={() => setBulkLogOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" disabled={saving} data-testid="bulk-contact-submit" className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{saving ? "Saving…" : `Log for ${selected.length}`}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyFor} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent data-testid="care-history-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Care history · {historyFor?.name}</DialogTitle></DialogHeader>
          <div className="mt-2 max-h-[60vh] overflow-y-auto pr-1">
            {historyLoading ? (
              <div className="py-10 text-center text-sm" style={{ color: "var(--sj-text-3)" }}>Loading…</div>
            ) : history.length === 0 ? (
              <div className="py-10 text-center text-sm flex flex-col items-center gap-2" style={{ color: "var(--sj-text-3)" }}>
                <ClipboardList className="h-10 w-10" /> No history yet for this member.
              </div>
            ) : (
              <div className="relative pl-6" data-testid="care-history-list">
                <div className="absolute left-[9px] top-1 bottom-1 w-px" style={{ background: "var(--sj-border)" }} />
                {history.map((e, i) => <HistoryEvent key={i} e={e} />)}
              </div>
            )}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setHistoryFor(null)} className="rounded-full px-6 py-2.5 text-white font-semibold" style={{ background: "var(--sj-primary)" }}>Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HistoryEvent({ e }) {
  const META = {
    call: { icon: PhoneCall, color: "#0B5E4E", label: "Call" },
    text: { icon: MessageSquare, color: "#17A88A", label: "Text" },
    email: { icon: Send, color: "#17A88A", label: "Email" },
    visit: { icon: MapPin, color: "#084438", label: "Visit" },
    prayer: { icon: HeartHandshake, color: "#8E44AD", label: "Prayer request" },
    note: { icon: StickyNote, color: "#56626A", label: "Note" },
    handover: { icon: ArrowRightCircle, color: "#8a6a00", label: "Handover note → Follow-up" },
  };
  const meta = META[e.type] || META.note;
  const Icon = meta.icon;
  const when = e.date ? new Date(e.date).toLocaleString('en-GB', { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className="relative mb-5" data-testid={`history-event-${e.type}`}>
      <div className="absolute -left-6 top-0.5 h-5 w-5 rounded-full flex items-center justify-center" style={{ background: `${meta.color}1A` }}>
        <Icon className="h-3 w-3" style={{ color: meta.color }} />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold" style={{ color: "var(--sj-text)" }}>{meta.label}</span>
        {e.outcome && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize" style={{ background: `${meta.color}14`, color: meta.color }}>{OUTCOME_LABEL[e.outcome] || e.outcome.replace(/_/g, " ")}</span>}
        {e.automated && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#0844381A", color: "#084438" }}>Automated</span>}
      </div>
      {e.note && <p className="text-sm mt-0.5" style={{ color: "var(--sj-text-2)" }}>{e.note}</p>}
      <div className="text-xs mt-0.5" style={{ color: "var(--sj-text-3)" }}>
        {when}{e.author && e.type !== "prayer" && e.type !== "note" ? ` · ${e.automated ? "Sent automatically" : `by ${e.author}`}${e.logged_by ? ` (logged by ${e.logged_by})` : ""}` : ""}
      </div>
    </div>
  );
}

function StageBadge({ stage }) {
  const map = {
    pastoral: ["Pastoral care", "#0B5E4E"],
    follow_up: ["Follow-Up", "#17A88A"],
    integrated: ["Integrated", "#A8A29E"],
  };
  const [label, color] = map[stage] || map.integrated;
  return <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ background: `${color}1A`, color }}>{label}</span>;
}

function MemberRow({ m, tab, onLog, onAction, onHistory, onFirstTimer, selected, onToggleSelect }) {
  const showAttempts = m.care_stage === "pastoral";
  const overdue = overdueDays(m);
  return (
    <div data-testid={`care-member-${m.id}`} className={`${CARD} p-4 flex flex-wrap items-center gap-4`} style={overdue ? { borderLeft: "4px solid #C0392B" } : selected ? { borderLeft: "4px solid var(--sj-primary)" } : undefined}>
      <input type="checkbox" data-testid={`care-select-${m.id}`} checked={!!selected} onChange={() => onToggleSelect(m.id)} className="h-4 w-4 accent-[#0B5E4E] shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold">{m.name}</span>
          <StageBadge stage={m.care_stage} />
          {m.is_convert && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#0844381A", color: "#084438" }}>New convert</span>}
          {m.is_first_timer && <span data-testid={`first-timer-badge-${m.id}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FEF6D8", color: "#8a6a00" }}>First-timer</span>}
          {overdue > 0 && (
            <span data-testid={`overdue-badge-${m.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#C0392B1A", color: "#C0392B" }}>
              <AlertCircle className="h-3 w-3" /> Overdue · {overdue}d
            </span>
          )}
        </div>
        {m.care_stage === "follow_up" && m.handover_note && <HandoverNote m={m} />}
        <div className="flex items-center gap-3 text-xs mt-1 flex-wrap" style={{ color: "var(--sj-text-3)" }}>
          {m.phone ? <PhoneActions phone={m.phone} name={m.name} onLogCall={() => onLog(m)} className="text-sm" /> : <span className="italic">No phone</span>}
          <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> Joined {ukDate(m.join_date)}</span>
          {m.last_contact_at ? (
            <span className="flex items-center gap-1">
              Last: {OUTCOME_LABEL[m.last_contact_outcome] || m.last_contact_kind}
              {m.last_contact_by && <> by <span className="font-semibold" style={{ color: "var(--sj-text-2)" }}>{m.last_contact_automated ? "Automated" : m.last_contact_by}</span></>}
              {" · "}{timeAgo(m.last_contact_at)}
            </span>
          ) : <span className="italic">Never contacted</span>}
          <span>{m.contact_count} logged</span>
        </div>
      </div>

      {showAttempts && (
        <div className="text-center shrink-0" title="Pastoral call attempts">
          <div className="font-head text-lg font-extrabold" style={{ color: m.pastoral_call_attempts >= m.call_target ? "#17A88A" : "#0B5E4E" }}>{m.pastoral_call_attempts}/{m.call_target}</div>
          <div className="text-[10px] uppercase tracking-wide" style={{ color: "var(--sj-text-3)" }}>calls</div>
        </div>
      )}

      <div data-testid={`caller-cell-${m.id}`} className="shrink-0 text-xs w-32 text-left" style={{ color: m.assigned_caller ? "var(--sj-text-2)" : "var(--sj-text-3)" }}>
        {m.assigned_caller ? <span data-testid={`assigned-caller-${m.id}`} className="inline-flex items-center gap-1 font-semibold"><UserPlus className="h-3 w-3" style={{ color: "var(--sj-primary)" }} /> {m.assigned_caller}</span> : "Unassigned"}
      </div>

      <div className="flex flex-wrap items-center gap-2 w-full sm:w-[280px] sm:shrink-0 justify-start">
        <button data-testid={`log-call-${m.id}`} onClick={() => onLog(m, "call")} className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-2 text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}><PhoneCall className="h-3.5 w-3.5" /> Call</button>
        <button data-testid={`log-text-${m.id}`} onClick={() => onLog(m, "text")} className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-2 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}><MessageSquare className="h-3.5 w-3.5" /> Text</button>
        <button data-testid={`history-${m.id}`} onClick={() => onHistory(m)} title="View care history" className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-2 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><History className="h-3.5 w-3.5" /> History</button>
        <button data-testid={`first-timer-toggle-${m.id}`} onClick={() => onFirstTimer(m)} title={m.is_first_timer ? "Remove first-timer tag" : "Mark as first-timer"} className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-2 border hover:bg-[#FEF6D8]" style={{ borderColor: "var(--sj-border)", color: "#8a6a00" }}><Star className="h-3.5 w-3.5" fill={m.is_first_timer ? "currentColor" : "none"} /> {m.is_first_timer ? "First-timer" : "Mark first-timer"}</button>
        {m.care_stage === "pastoral" && (
          <button data-testid={`handoff-${m.id}`} onClick={() => onAction(m, "handover")} title="Hand off to Follow-Up team" className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-2 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><ArrowRightCircle className="h-3.5 w-3.5" /> Hand off</button>
        )}
        {m.care_stage === "follow_up" && (
          <button data-testid={`integrate-${m.id}`} onClick={() => onAction(m, "integrate")} title="Mark as integrated" className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-2 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "#17A88A" }}><UserCheck className="h-3.5 w-3.5" /> Integrated</button>
        )}
        {m.care_stage === "integrated" && (
          <button data-testid={`reopen-${m.id}`} onClick={() => onAction(m, "back-to-pastoral")} title="Reopen pastoral care" className="flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-2 border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><RotateCcw className="h-3.5 w-3.5" /> Reopen</button>
        )}
      </div>
    </div>
  );
}

function ActivityFeed({ activities }) {
  if (activities.length === 0) {
    return <div className={`${CARD} p-16 text-center flex flex-col items-center gap-3`} style={{ color: "var(--sj-text-3)" }}><ClipboardList className="h-12 w-12" /> No activity logged yet.</div>;
  }
  return (
    <div className={`${CARD} divide-y`} style={{ borderColor: "var(--sj-border)" }} data-testid="activity-list">
      {activities.map((a) => {
        const KIcon = KIND_ICON[a.kind] || PhoneCall;
        const OIcon = OUTCOME_ICON[a.outcome] || Clock;
        const teamColor = a.team === "follow_up" ? "#17A88A" : "#0B5E4E";
        return (
          <div key={a.id} data-testid={`activity-${a.id}`} className="flex items-start gap-3 p-4">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${teamColor}1A` }}><KIcon className="h-4.5 w-4.5" style={{ color: teamColor }} /></div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm">{a.member_name}</span>
                <span className="text-xs flex items-center gap-1" style={{ color: "var(--sj-text-3)" }}><OIcon className="h-3.5 w-3.5" /> {OUTCOME_LABEL[a.outcome] || a.outcome}</span>
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${teamColor}1A`, color: teamColor }}>{a.team === "follow_up" ? "Follow-Up" : "Pastoral"}</span>
                {a.automated && <span data-testid={`automated-badge-${a.id}`} className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#0844381A", color: "#084438" }}>Automated</span>}
              </div>
              {a.note && <p className="text-sm mt-0.5" style={{ color: "var(--sj-text-2)" }}>{a.note}</p>}
              <div className="text-xs mt-0.5" style={{ color: "var(--sj-text-3)" }}>{a.automated ? "Sent automatically" : `by ${a.author}`}{a.logged_by ? ` (logged by ${a.logged_by})` : ""} · {a.created_at ? new Date(a.created_at).toLocaleString('en-GB', { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
